<template>
  <v-card class="pb-5" elevation="0">
    <v-card-text class="ma-0 pa-5">
      <v-form ref="form" v-model="valid">
        <v-row>
          <v-divider />
        </v-row>

        <DateRangePicker
          ref="datesPicker"
          :dateEvents="dateEvents"
          @change="datesChanged"
        />

        <v-row v-if="!atLeastOneHour">
          <v-col class="text-subtitle-1 mt-0 pt-0 red--text">
            {{ $t("booking.minHoursNok") }}
          </v-col>
        </v-row>

        <v-row v-if="!desksAvailability.value && equipmentsAvailability.value">
          <v-col class="text-subtitle-1 mt-0 pt-0 red--text">
            {{ $t("booking.noDesksAreBookable") }}
          </v-col>
        </v-row>

        <v-row
          v-else-if="desksAvailability.value && !equipmentsAvailability.value"
        >
          <v-col class="text-subtitle-1 mt-0 pt-0 red--text">
            {{ $t("booking.noEquipmentsAreBookable") }}
          </v-col>
        </v-row>

        <v-row
          v-else-if="!desksAvailability.value && !equipmentsAvailability.value"
        >
          <v-col class="text-subtitle-1 mt-0 pt-0 red--text">
            {{ $t("booking.noDesksOrEquipmentsAreBookable") }}
          </v-col>
        </v-row>

        <v-row>
          <v-divider />
        </v-row>

        <v-row class="mb-0 pb-0">
          <v-col class="mb-0 pb-0">
            <v-select
              v-model="reason"
              :items="reasons"
              :label="$t('booking.purpose')"
              color="grey"
              required
              @input="$v.reason.$touch()"
              @blur="$v.reason.$touch()"
              :error-messages="reasonErrors"
            />
          </v-col>
        </v-row>

        <v-row class="mt-0 pt-0 mb-0 pb-0" v-if="isTraining">
          <v-col class="mt-0 pt-0 mb-0 pb-0">
            <v-text-field
              v-model.number="booking.numUsers"
              :label="$t('booking.numUsers')"
              color="grey"
              required
              @input="$v.numUsers.$touch()"
              @blur="$v.numUsers.$touch()"
              :error-messages="numUsersErrors"
              :rules="rules.inputNumber"
            />
          </v-col>
        </v-row>

        <v-row class="mt-0 pt-0 mb-0 pb-0">
          <v-col class="mt-0 pt-0 mb-0 pb-0">
            <v-text-field
              v-model="booking.complement"
              :label="$t('booking.complement')"
              color="grey"
              @input="$v.complement.$touch()"
              @blur="$v.complement.$touch()"
              :error-messages="complementErrors"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="ma-0 pa-0">
      <v-spacer />

      <v-btn color="red darken-1" text @click="cancelBooking">
        {{ $t("booking.stepperCancel") }}
      </v-btn>
      <v-btn color="red darken-1" text :disabled="!canGoNext" @click="goNext">
        {{ $t("booking.stepperNext") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import DateRangePicker from "../DateRangePicker";
import { mapGetters } from "vuex";
import i18n from "@/i18n";
import BookingReasons from "@/models/BookingReasons";

import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import rules from "@/models/InputRules";

const complementMaxLength = 100;

export default {
  name: "BookingStep",
  components: {
    DateRangePicker,
  },
  mixins: [validationMixin],
  validations: {
    numUsers: { required },
    reason: { required },
    complement: { maxLength: maxLength(complementMaxLength) },
  },
  props: {
    booking: null,
    desksAvailability: null,
    equipmentsAvailability: null,
  },
  data: () => ({
    valid: false,
    rules: rules,
    reasons: i18n.t("booking.purposes"),
    reason: null,
  }),
  computed: {
    ...mapGetters("bookingModule", {
      bookings: "getBookings",
      getRoomBookingsByTimeSlots: "getRoomBookingsByTimeSlots",
    }),
    ...mapGetters("deskModule", {
      availableDesksByRoom: "getAvailableDesksByRoom",
    }),

    // validation
    numUsersErrors() {
      const errors = [];
      if (this.isTraining && this.$v.numUsers.$dirty) {
        !this.$v.numUsers.required &&
          this.booking.numUsers <= 0 &&
          errors.push(i18n.t("errorFrontEnd.fieldRequired"));
      }
      return errors;
    },
    reasonErrors() {
      const errors = [];
      if (this.$v.reason.$dirty) {
        !this.$v.reason.required &&
          errors.push(i18n.t("errorFrontEnd.fieldRequired"));
      }
      return errors;
    },
    complementErrors() {
      const errors = [];
      if (this.$v.complement.$dirty) {
        !this.$v.complement.maxLength &&
          errors.push(
            i18n
              .t("errorFrontEnd.fieldLength")
              .replace("%1", complementMaxLength)
          );
      }
      return errors;
    },

    anyError() {
      return !this.valid || !this.atLeastOneHour;
    },
    canGoNext() {
      return (
        !this.anyError &&
        ((this.isTraining && this.booking.numUsers) || !this.isTraining) &&
        this.reason
      );
    },

    readableDates() {
      return this.$refs.datesPicker.dateRangeReadable;
    },
    atLeastOneHour() {
      return this.booking.interval?.length("hours") >= 1;
    },
    isTraining() {
      return this.booking.reason == "TRAINING";
    },
  },
  methods: {
    // navigation
    reset() {
      // validation
      this.$refs.form.resetValidation();
      this.$v.$reset();

      this.$refs.datesPicker.reset();

      this.reason = null;
    },
    show() {
      this.$refs.datesPicker.onDateTimeInput();
    },
    hide() {},
    cancelBooking() {
      this.$emit("cancelBooking");
    },
    goNext() {
      this.$emit("goNext");
    },

    // room availability by date
    availableDesks() {
      return this.availableDesksByRoom(this.booking.room);
    },
    roomBookingsByTimeSlots(date) {
      return this.getRoomBookingsByTimeSlots(this.booking.room, date);
    },
    dateEvents(date) {
      // no bookings
      if (!this.bookings.length) return null;
      const numAvailDesks = this.availableDesks().length;
      const roomBookings = this.roomBookingsByTimeSlots(date);
      // no bookings for this room
      if (!roomBookings) return null;

      // the room has no desks
      if (numAvailDesks === 0) {
        // all time slots are booked
        const every = roomBookings.slots.every((x) => x.any);
        if (every) return "red";
        // some time slots are booked
        const some = roomBookings.slots.some((x) => x.any);
        if (some) return "orange";
      } else {
        // sum booked desks on all time slots
        let numDesks = 0;
        roomBookings.slots.forEach(
          (x) => (numDesks += x.whole ? numAvailDesks : x.desks.size)
        );
        // no desks are booked
        if (numDesks === 0) return null;
        // the sum is less than the count of desks available the whole day
        if (numDesks < numAvailDesks * 18) return "orange";
        // all desks are booked on the whole day
        else return "red";
      }

      // default color
      return null;
    },
    datesChanged(interval) {
      this.booking.interval = interval;
      this.booking.readableDates = this.readableDates;
    },
  },
  watch: {
    // store db equivalent of localized reason (e.g. "Formation" -> "TRAINING")
    reason(newValue) {
      this.booking.reason = BookingReasons[this.reasons.indexOf(newValue)];
      this.booking.readableReason = newValue;
    },
  },
};
</script>
